/**
 * Filters a text based on the leading asterisks in the query, ignoring case.
 *
 * @param {string} text - The text to be evaluated.
 * @param {string} query - The query string which may contain leading asterisks.
 * @returns {boolean} - Returns true if the text matches the query criteria; otherwise, false.
 *
 *  * Rules:
 * - No `*` → text must start with query.
 * - Leading `*` → each star skips one character from text before matching.
 *
 * @example
 * isTextMatchingLeadingStarsFilterHelper("HelloWorld", "He");   // true
 * isTextMatchingLeadingStarsFilterHelper("HelloWorld", "*e");   // true
 * isTextMatchingLeadingStarsFilterHelper("HelloWorld", "**l");  // true
 * isTextMatchingLeadingStarsFilterHelper("HelloWorld", "Wo");   // false
 */
const isTextMatchingLeadingStarsFilterHelper = (
	text: string,
	query: string,
): boolean => {
	// Ensure both text and query are strings
	if (typeof text !== "string" || typeof query !== "string") {
		return false;
	}

	// Normalize case without mutating original arguments
	const normalizedText = text.toLowerCase();
	const normalizedQuery = query.toLowerCase();

	// Determine the number of leading asterisks in the query
	const leadingStarsCount = normalizedQuery.match(/^(\*+)/)?.[1].length || 0;

	// If there are no leading asterisks, check if text starts with the query
	if (leadingStarsCount === 0) {
		return normalizedText.startsWith(normalizedQuery);
	}

	// If text is shorter than or equal to the number of leading asterisks, it cannot be a match
	if (normalizedText.length <= leadingStarsCount) {
		return false;
	}

	// Remove the leading asterisks from the query and the corresponding number of characters from text
	const queryWithoutStars = normalizedQuery.slice(leadingStarsCount);
	const textWithoutStars = normalizedText.slice(leadingStarsCount);

	// Check if the adjusted text starts with the adjusted query
	return textWithoutStars.startsWith(queryWithoutStars);
};

export default isTextMatchingLeadingStarsFilterHelper;
